// A client pipeline board: deals in stages the user names, each with its next step and when, its history
// (calls, notes, what was agreed), and whether it was won or lost. The stages and the pipeline's currency are
// one entry ("stages"); each deal is an entry ("deal"). Shared by the board, the server and the agent tools,
// so all three say the same thing about the same deal.

import type { Entry, ID, WidgetItem } from "./types";
import { dayIn } from "./brief";
import { cleanPictureRef, picturePath, type PictureRef } from "./imageInfo";
import { DAY } from "./period";

export interface Stage {
  id: string;
  name: string;
  /** the stage a won deal sits in: one at most, the last by default */
  won?: true;
}

export interface StagesData {
  stages: Stage[];
  /** ISO 4217, e.g. USD: every deal's value is in it */
  currency: string;
  /** the time zone the user's days are in, for "today" and "2 days late" when the board answers an agent */
  tz: string | null;
}

export type DealStatus = "open" | "won" | "lost";
/** a line of a deal's history: written by someone, or the board's own note of a move */
export type NoteKind = "note" | "call" | "email" | "meeting" | "agreed" | "moved";

export interface DealNote {
  id: string;
  at: number;
  kind: NoteKind;
  text: string;
  /** an agent's name when one wrote it; null for the user */
  by: string | null;
  /** a picture the board keeps, with the note's words as its caption (Features 2, 21 Oct) */
  image?: NoteImage;
}

/** A picture in a deal's history: one the board keeps as a page's pictures are, by its key, and its size. */
export type NoteImage = PictureRef;
export const cleanNoteImage = cleanPictureRef;

export interface DealData {
  company: string;
  contact: string;
  email: string;
  phone: string;
  /** what the deal is worth, in the pipeline's currency; null when nobody said */
  value: number | null;
  /** a stage's id */
  stage: string;
  status: DealStatus;
  /** when it was won or lost */
  closedAt: number | null;
  /** the next step, in a few words */
  next: string;
  /** the next step's date, YYYY-MM-DD, in the user's own days */
  due: string | null;
  /** newest last, as they happened */
  notes: DealNote[];
}

export const NOTE_KINDS: NoteKind[] = ["note", "call", "email", "meeting", "agreed"];
export const NOTE_WORDS: Record<NoteKind, string> = { note: "Note", call: "Call", email: "Email", meeting: "Meeting", agreed: "Agreed", moved: "Moved" };
export const MAX_STAGES = 12;
export const MAX_NOTES = 400;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const NOTE_ID = /^[A-Za-z0-9_-]{2,40}$/;
const STAGE_ID = /^[A-Za-z0-9_-]{2,40}$/;

/** What a new pipeline starts with: four stages, the last one where won deals go. */
export const DEFAULT_STAGES: Stage[] = [
  { id: "lead", name: "Lead" },
  { id: "talking", name: "Talking" },
  { id: "proposal", name: "Proposal sent" },
  { id: "won", name: "Won", won: true },
];

export const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "NZD", "CHF", "SEK", "NOK", "DKK", "JPY", "INR", "SGD", "BRL", "MXN", "ZAR"];

// ---------------------------------------------------------------------------
// a pipeline's currency (Fixes 9, 28 Oct: a UK account's pipeline said USD and turned £1,200 into $1,200)

/** The UK's time zones, and the euro area's (Bulgaria's since 1 Jan 2026), as an account's region says them. */
const UK_ZONES = /^(Europe\/(London|Belfast|Jersey|Guernsey|Isle_of_Man)|GB|GB-Eire)$/;
const EURO_ZONES = /^(Europe\/(Amsterdam|Andorra|Athens|Berlin|Bratislava|Brussels|Busingen|Dublin|Helsinki|Lisbon|Ljubljana|Luxembourg|Madrid|Malta|Mariehamn|Monaco|Nicosia|Paris|Podgorica|Riga|Rome|San_Marino|Sofia|Tallinn|Vatican|Vienna|Vilnius|Zagreb)|Asia\/(Nicosia|Famagusta)|Atlantic\/(Azores|Madeira|Canary)|Africa\/Ceuta|Eire|Portugal)$/;

/**
 * The currency a new pipeline starts in, from the account's region: the time zone its browser last said it is in (the
 * server keeps it, kv "tz"), the same source a sheet reads dates by (shared/sheet/model.ts regionDayFirst). The UK is
 * pounds, the euro area euros, and anywhere else, the United States included, dollars. It is changed in the
 * pipeline's Stages and currency window, or by update_board.
 */
export function regionCurrency(tz?: string | null): string {
  let zone = tz || null;
  if (!zone)
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      zone = null;
    }
  if (!zone) return "USD";
  if (UK_ZONES.test(zone)) return "GBP";
  if (EURO_ZONES.test(zone)) return "EUR";
  return "USD";
}

/** A currency's sign as a value shows it: £, €, $, CA$. */
export function currencySign(currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).formatToParts(0).find((x) => x.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
}

/** Codes a written value may carry, beyond the ones a pipeline offers. */
const OTHER_CODES = ["CNY", "HKD", "KRW", "PLN", "CZK", "HUF", "ILS", "AED", "SAR", "TRY", "RUB", "THB", "IDR", "MYR", "ARS", "CLP", "COP", "NGN", "KES", "EGP", "TWD", "VND", "ISK", "RON", "BGN", "UAH"];
const DOLLARS = ["USD", "CAD", "AUD", "NZD", "SGD", "MXN"];
const KRONOR = ["SEK", "NOK", "DKK"];
/** Signs before or after the number, longest first so "CA$" is not read as "$". */
const SIGNS: [RegExp, string[]][] = [
  [/US\$/i, ["USD"]],
  [/(?:CA|C)\$/i, ["CAD"]],
  [/(?:AU|A)\$/i, ["AUD"]],
  [/NZ\$/i, ["NZD"]],
  [/S\$/i, ["SGD"]],
  [/MX\$/i, ["MXN"]],
  [/R\$/i, ["BRL"]],
  [/\$/, DOLLARS],
  [/£/, ["GBP"]],
  [/€/, ["EUR"]],
  [/[¥￥]/, ["JPY", "CNY"]],
  [/₹/, ["INR"]],
  [/₩/, ["KRW"]],
  [/₺/, ["TRY"]],
  [/₽/, ["RUB"]],
  [/zł/i, ["PLN"]],
];
/** Words for money, as people write them after a number ("500 euros", "2k quid"). */
const WORDS: [RegExp, string[]][] = [
  [/\b(dollars?|bucks?|usd)\b/i, DOLLARS],
  [/\b(pounds?|quid|sterling|gbp)\b/i, ["GBP"]],
  [/\b(euros?|eur)\b/i, ["EUR"]],
  [/\byen\b/i, ["JPY"]],
  [/\brupees?\b/i, ["INR"]],
  [/\b(francs?|fr\.?|chf)(?=\s|$|\d)/i, ["CHF"]],
  [/\b(kr\.?|krona|kronor|krone|kroner)(?=\s|$|\d)/i, KRONOR],
  [/\brand\b/i, ["ZAR"]],
  [/\b(reais|real)\b/i, ["BRL"]],
  [/\bpesos?\b/i, ["MXN"]],
];

/**
 * The currencies a written value names by its sign, code or word: "$500" any dollar, "£1,200" pounds, "500 euros" euros,
 * "1200 GBP" pounds. Empty for a plain number, or a value that names none.
 */
export function currenciesNamed(raw: unknown): string[] {
  if (typeof raw !== "string") return [];
  const v = raw.trim();
  if (!v || /^-?[\d.,\s]+[kKmM]?$/.test(v)) return [];
  const out = new Set<string>();
  let rest = v;
  for (const [re, codes] of SIGNS) {
    if (!re.test(rest)) continue;
    for (const c of codes) out.add(c);
    rest = rest.replace(new RegExp(re.source, re.flags.includes("g") ? re.flags : re.flags + "g"), " ");
  }
  for (const m of v.toUpperCase().matchAll(/\b([A-Z]{3})\b/g)) if (CURRENCIES.includes(m[1]) || OTHER_CODES.includes(m[1])) out.add(m[1]);
  for (const [re, codes] of WORDS) if (re.test(v)) for (const c of codes) out.add(c);
  // a bare R before the number is the South African rand ("R2,500"), once R$ has been read as reais
  if (/^R\s?\d/.test(rest.trim())) out.add("ZAR");
  return [...out];
}

/**
 * A line refusing a value written in another currency than the pipeline's ("$500" on a pound pipeline), or null when
 * it names none or the pipeline's own. The value is never turned into the pipeline's currency: no rate is guessed.
 */
export function otherCurrency(raw: unknown, currency: string): string | null {
  const named = currenciesNamed(raw);
  if (!named.length || named.includes(currency)) return null;
  const sign = currencySign(currency);
  const was = named.length === 1 ? named[0] : named.every((c) => DOLLARS.includes(c)) ? "dollars" : named.every((c) => KRONOR.includes(c)) ? "kronor" : named.join(" or ");
  const n = parseValue(raw);
  return `"${String(raw).trim()}" is in ${was}, and this pipeline's values are in ${currency}${sign !== currency ? ` (${sign})` : ""}. Give the value in ${currency}${n !== null ? `, as a number like ${n}` : ""}, or change the pipeline's currency first.`;
}

/** A currency someone named for a pipeline ("GBP", "£", "pounds", "euro"): its code, or null when it is none or several. */
export function currencyOf(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const v = raw.trim();
  const code = v.toUpperCase();
  if (/^[A-Z]{3}$/.test(code)) {
    let known: string[] = [];
    try {
      known = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("currency") ?? [];
    } catch {
      known = [];
    }
    return CURRENCIES.includes(code) || OTHER_CODES.includes(code) || known.includes(code) ? code : null;
  }
  const named = currenciesNamed(/^[\d]/.test(v) ? v : `1 ${v}`);
  // "$" or "dollars" alone: US dollars
  if (named.length > 1 && named.includes("USD") && named.every((c) => DOLLARS.includes(c))) return "USD";
  return named.length === 1 ? named[0] : null;
}

/** Why a change was refused, in words a person or an agent reads (shared/entries.ts turns it into its own error). */
export class PipelineRefused extends Error {}

const str = (v: unknown, max: number): string => (typeof v === "string" ? v.replace(/[\r\n\t]+/g, " ").trim().slice(0, max) : "");

/** A number someone typed or pasted as a value: "$2,500", "2.5k", 2500. null when it is not one. */
export function parseValue(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 && v <= 1e12 ? Math.round(v * 100) / 100 : null;
  if (typeof v !== "string") return null;
  const m = v.replace(/[\s,]/g, "").replace(/^[^\d.-]+/, "").match(/^(-?\d*\.?\d+)([kKmM]?)/);
  if (!m) return null;
  const n = Number(m[1]) * (m[2] === "k" || m[2] === "K" ? 1e3 : m[2] === "m" || m[2] === "M" ? 1e6 : 1);
  return parseValue(n);
}

function cleanNote(raw: unknown): DealNote | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const text = typeof r.text === "string" ? r.text.trim().slice(0, 4000) : "";
  const image = cleanNoteImage(r.image);
  // a picture may stand alone, its words empty
  if ((!text && !image) || typeof r.id !== "string" || !NOTE_ID.test(r.id)) return null;
  const kind = (NOTE_KINDS as string[]).includes(r.kind as string) || r.kind === "moved" ? (r.kind as NoteKind) : "note";
  const at = typeof r.at === "number" && Number.isFinite(r.at) ? r.at : 0;
  return { id: r.id, at, kind, text, by: typeof r.by === "string" && r.by ? r.by.slice(0, 60) : null, ...(image && kind !== "moved" ? { image } : {}) };
}

/** A deal's fields, kept to what a deal has. A field left out keeps what it was. A deal needs a name: its company or its contact. */
export function cleanDeal(raw: Record<string, unknown>, before: DealData | null): DealData {
  const has = (k: string) => Object.hasOwn(raw, k);
  const company = has("company") ? str(raw.company, 120) : (before?.company ?? "");
  const contact = has("contact") ? str(raw.contact, 120) : (before?.contact ?? "");
  if (!company && !contact) throw new PipelineRefused("A deal needs a name: its company or its contact.");
  const status: DealStatus = has("status") ? (raw.status === "won" || raw.status === "lost" ? raw.status : "open") : (before?.status ?? "open");
  const closed = has("closedAt") ? (typeof raw.closedAt === "number" && Number.isFinite(raw.closedAt) ? raw.closedAt : null) : (before?.closedAt ?? null);
  const due = has("due") ? (typeof raw.due === "string" && DATE.test(raw.due) ? raw.due : null) : (before?.due ?? null);
  let notes = before?.notes ?? [];
  if (has("notes")) {
    if (!Array.isArray(raw.notes)) throw new PipelineRefused("A deal's notes are a list.");
    const seen = new Set<string>();
    notes = (raw.notes as unknown[]).flatMap((n) => {
      const c = cleanNote(n);
      if (!c || seen.has(c.id)) return [];
      seen.add(c.id);
      return [c];
    });
    notes = notes.slice(-MAX_NOTES);
  }
  return {
    company,
    contact,
    email: has("email") ? str(raw.email, 200) : (before?.email ?? ""),
    phone: has("phone") ? str(raw.phone, 60) : (before?.phone ?? ""),
    value: has("value") ? parseValue(raw.value) : (before?.value ?? null),
    stage: has("stage") ? (typeof raw.stage === "string" && STAGE_ID.test(raw.stage) ? raw.stage : (before?.stage ?? DEFAULT_STAGES[0].id)) : (before?.stage ?? DEFAULT_STAGES[0].id),
    status,
    closedAt: status === "open" ? null : closed,
    next: has("next") ? str(raw.next, 200) : (before?.next ?? ""),
    due,
    notes,
  };
}

/** A pipeline's stages: one to MAX_STAGES, each with an id and a name, one at most where won deals go. */
export function cleanStages(raw: Record<string, unknown>, before: StagesData | null): StagesData {
  let stages = before?.stages ?? DEFAULT_STAGES;
  if (Object.hasOwn(raw, "stages")) {
    if (!Array.isArray(raw.stages)) throw new PipelineRefused("A pipeline's stages are a list.");
    const seen = new Set<string>();
    let won = false;
    stages = (raw.stages as unknown[]).flatMap((s) => {
      if (!s || typeof s !== "object") return [];
      const r = s as Record<string, unknown>;
      const name = str(r.name, 40);
      if (!name || typeof r.id !== "string" || !STAGE_ID.test(r.id) || seen.has(r.id)) return [];
      seen.add(r.id);
      const isWon = r.won === true && !won;
      if (isWon) won = true;
      return [isWon ? { id: r.id, name, won: true as const } : { id: r.id, name }];
    });
    if (!stages.length) throw new PipelineRefused("A pipeline needs at least one stage.");
    if (stages.length > MAX_STAGES) throw new PipelineRefused(`A pipeline has up to ${MAX_STAGES} stages.`);
  }
  const currency = typeof raw.currency === "string" && /^[A-Z]{3}$/.test(raw.currency) ? raw.currency : (before?.currency ?? "USD");
  const tz = Object.hasOwn(raw, "tz") ? (typeof raw.tz === "string" && raw.tz.length <= 60 && validZone(raw.tz) ? raw.tz : null) : (before?.tz ?? null);
  return { stages, currency, tz };
}

/** A time zone this runtime knows ("Europe/London"); one only shaped like one ("Foo/Bar") is not. */
export function validZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// reading a pipeline

export type Deal = Entry & { data: DealData };

export const isDeal = (e: Entry | null | undefined): e is Deal => !!e && e.t === "deal";

/** A pipeline's stages entry and what it says, or the defaults when it has none yet. */
export function stagesOf(entries: Entry[]): { entry: Entry | null; data: StagesData } {
  const entry = entries.find((e) => e.t === "stages") ?? null;
  const d = entry?.data as Partial<StagesData> | undefined;
  return { entry, data: { stages: d?.stages?.length ? d.stages : DEFAULT_STAGES, currency: d?.currency ?? "USD", tz: d?.tz ?? null } };
}

/** The stage a deal is in: its own, or the first when its stage has gone. A won deal sits in the won stage when there is one. */
export function stageOf(d: DealData, stages: Stage[]): Stage {
  if (d.status === "won") {
    const won = stages.find((s) => s.won);
    if (won) return won;
  }
  return stages.find((s) => s.id === d.stage) ?? stages[0];
}

/** A deal's name as people read it: its company, else its contact. */
export function dealName(d: Pick<DealData, "company" | "contact">): string {
  return d.company || d.contact || "Untitled deal";
}

/** The deals on a pipeline, open ones by stage in their order, as the columns show them. */
export function dealsOf(entries: Entry[]): Deal[] {
  return entries.filter(isDeal).sort((a, b) => a.order - b.order || a.createdAt - b.createdAt || (a.id < b.id ? -1 : 1));
}

/** A column: the deals in a stage, open ones (and won ones in the won stage); lost deals sit apart. */
export function columnOf(deals: Deal[], stage: Stage, stages: Stage[]): Deal[] {
  return deals.filter((d) => d.data.status !== "lost" && stageOf(d.data, stages).id === stage.id);
}

/** Every deal on a pipeline in the order the board shows them: stage by stage, each column in its order, then the lost ones. */
export function byStage(entries: Entry[]): Deal[] {
  const { stages } = stagesOf(entries).data;
  const deals = dealsOf(entries);
  return [...stages.flatMap((s) => columnOf(deals, s, stages)), ...deals.filter((d) => d.data.status === "lost")];
}

/** The sum of what deals are worth, of the ones that say; null when none says. */
export function totalOf(deals: Deal[]): number | null {
  const valued = deals.filter((d) => d.data.value !== null);
  return valued.length ? valued.reduce((n, d) => n + (d.data.value ?? 0), 0) : null;
}

/** What the open deals are worth together: null when none of them says. */
export const openValue = (deals: Deal[]): number | null => totalOf(deals.filter((d) => d.data.status === "open"));

/** A value in the pipeline's currency, whole when it is, and always in full: "$2,500", "$1,000,000", "€1,200.50". */
export function money(v: number | null | undefined, currency = "USD"): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: v % 1 ? 2 : 0, minimumFractionDigits: v % 1 ? 2 : 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString("en-US")}`;
  }
}

// ---------------------------------------------------------------------------
// days: a next step's date against the user's today

/** Today in the user's own days, YYYY-MM-DD: the browser's, or a time zone's when the server answers. */
export function todayKey(now = Date.now(), tz?: string | null): string {
  if (tz) {
    try {
      return dayIn(now, tz);
    } catch {
      /* the browser's own below */
    }
  }
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const dayNum = (key: string) => Math.round(Date.UTC(+key.slice(0, 4), +key.slice(5, 7) - 1, +key.slice(8, 10)) / DAY);
const keyOf = (n: number) => new Date(n * DAY).toISOString().slice(0, 10);

/** Days from today to a date: 0 today, negative when it has gone. */
export const daysFrom = (due: string, today: string): number => dayNum(due) - dayNum(today);

export type DueTone = "late" | "today" | "soon" | "later" | "none";

/** A next step's date in words, as a card says it: "Today", "Tomorrow", "Thu", "Oct 22", "2 days late", "No next step". */
export function dueWords(d: Pick<DealData, "next" | "due" | "status">, today: string): { text: string; tone: DueTone } | null {
  if (d.status !== "open") return null;
  if (!d.due) return d.next ? null : { text: "No next step", tone: "none" };
  const n = daysFrom(d.due, today);
  if (n < 0) return { text: n === -1 ? "1 day late" : `${-n} days late`, tone: "late" };
  if (n === 0) return { text: "Today", tone: "today" };
  if (n === 1) return { text: "Tomorrow", tone: "soon" };
  const at = new Date(dayNum(d.due) * DAY);
  if (n < 7) return { text: at.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }), tone: "soon" };
  const sameYear = d.due.slice(0, 4) === today.slice(0, 4);
  return { text: at.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), timeZone: "UTC" }), tone: "later" };
}

/** A date in a few words: "Oct 9", or "Oct 9, 2025" in another year. */
export function dateWords(ms: number, now = Date.now()): string {
  const d = new Date(ms);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(d.getFullYear() === new Date(now).getFullYear() ? {} : { year: "numeric" }) });
}

/**
 * What to follow up, of the open deals: late (the date has gone), today, and no next step (none written and no
 * date). A deal with a step and no date waits for nobody, so it is not here.
 */
export function followUps(deals: Deal[], today: string): { late: Deal[]; today: Deal[]; none: Deal[]; count: number } {
  const open = deals.filter((d) => d.data.status === "open");
  const late = open.filter((d) => d.data.due && daysFrom(d.data.due, today) < 0).sort((a, b) => (a.data.due! < b.data.due! ? -1 : 1));
  const now = open.filter((d) => d.data.due === today);
  const none = open.filter((d) => !d.data.due && !d.data.next);
  return { late, today: now, none, count: late.length + now.length + none.length };
}

/**
 * A date an agent or a file gives for a next step, as YYYY-MM-DD in the user's days: a date as it is, today,
 * tomorrow, or a weekday (the next one after today). null for none; undefined when it cannot be read.
 */
export function readDue(v: unknown, today: string): string | null | undefined {
  if (v === null) return null;
  if (typeof v !== "string") return undefined;
  const s = v.trim().toLowerCase();
  if (!s || s === "none" || s === "clear") return null;
  if (DATE.test(s)) return s;
  if (s === "today") return today;
  if (s === "tomorrow") return keyOf(dayNum(today) + 1);
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const named = days.findIndex((d) => d === s || d.slice(0, 3) === s.replace(/^next /, "").slice(0, 3));
  if (named >= 0 && /^(next )?[a-z]+$/.test(s)) {
    const dow = new Date(dayNum(today) * DAY).getUTCDay();
    const ahead = ((named - dow + 7) % 7) || 7;
    return keyOf(dayNum(today) + ahead);
  }
  // 10/16/2026 (American), 16 Oct 2026, October 16, 2026
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (us) {
    const y = us[3].length === 2 ? 2000 + Number(us[3]) : Number(us[3]);
    const m = Number(us[1]);
    const d = Number(us[2]);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return undefined;
  }
  const t = Date.parse(`${v} UTC`);
  if (Number.isFinite(t) && /[a-z]/.test(s) && /\d/.test(s)) return new Date(t).toISOString().slice(0, 10);
  return undefined;
}

// ---------------------------------------------------------------------------
// the shelf's numbers: streams the board keeps from the deals themselves

/** The streams a pipeline keeps current: what is open, and what was won this quarter. */
export const pipelineStreamKeys = (boardId: ID) => ({ open: `pipeline-open-${boardId.toLowerCase()}`.slice(0, 64), won: `pipeline-won-${boardId.toLowerCase()}`.slice(0, 64) });

export const isPipelineStream = (key: string | null | undefined): boolean => !!key && /^pipeline-(open|won)-/.test(key);

/** The first day of the quarter a time is in, and of the next, UTC (as the goal's own calendar periods). */
export function quarterSpan(now = Date.now()): { from: number; end: number } {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() - (d.getUTCMonth() % 3);
  return { from: Date.UTC(y, m, 1), end: Date.UTC(y, m + 3, 1) };
}

/** What was won this quarter: the sum of the deals won in it that say what they are worth. */
export function wonThisQuarter(deals: Deal[], now = Date.now()): number {
  const q = quarterSpan(now);
  return deals.filter((d) => d.data.status === "won" && d.data.value !== null && (d.data.closedAt ?? 0) >= q.from && (d.data.closedAt ?? 0) < q.end).reduce((n, d) => n + (d.data.value ?? 0), 0);
}

/**
 * The shelf's numbers from the deals, or null for each the board cannot say yet: until a deal says what it is
 * worth there is no total to show, so nothing is sent and the widget says what it waits for.
 */
export function shelfNumbers(deals: Deal[], now = Date.now()): { open: number | null; won: number | null } {
  if (!deals.some((d) => d.data.value !== null)) return { open: null, won: null };
  return { open: openValue(deals) ?? 0, won: wonThisQuarter(deals, now) };
}

/** The shelf down the right of a pipeline: its widgets as wide as it is, each its own height, in the order of their y. */
export const SHELF_W = 320;

/** Whether the workspace's mrr stream holds anything: a number or a time it was filled. */
export function hasMrr(s: { value?: unknown; updatedAt?: number | null } | null | undefined): boolean {
  return !!s && (!!s.updatedAt || (s.value !== null && s.value !== undefined));
}

/**
 * What a new pipeline's shelf holds: what is open (with its history) and what was won this quarter as a goal (its
 * target the user's to set). MRR comes too only when the account's mrr stream has data (Fixes 9, 28 Oct: a freelancer
 * with no subscriptions got an empty number on their deals to remove by hand), filled by Stripe or Lemon Squeezy.
 */
export function pipelineShelf(boardId: ID, now: number, newId: () => ID, mrr = false): WidgetItem[] {
  const keys = pipelineStreamKeys(boardId);
  const make = (type: WidgetItem["type"], title: string, stream: string, h: number, y: number, props: Record<string, unknown>): WidgetItem => ({ id: newId(), boardId, kind: "widget", type, title, x: 0, y, w: SHELF_W, h, z: 1, props, stream, style: {}, createdAt: now, updatedAt: now, by: null });
  return [
    make("metric", "Open pipeline", keys.open, 176, 0, { compare: "auto", goodWhen: "up", spark: true }),
    make("goal", "Won this quarter", keys.won, 168, 192, { style: "bar" }),
    ...(mrr ? [make("metric", "MRR", "mrr", 176, 376, { compare: "auto", goodWhen: "up", spark: true })] : []),
  ];
}

// ---------------------------------------------------------------------------
// CSV: out, and in

export const CSV_COLUMNS = ["Company", "Contact", "Email", "Phone", "Value", "Stage", "Status", "Next step", "Date", "Won or lost on", "History"];

const cell = (v: string) => (/[",\r\n]/.test(v) || /^[=+\-@]/.test(v) ? `"${(/^[=+\-@]/.test(v) ? `'${v}` : v).replace(/"/g, '""')}"` : v);

/** One deal as a CSV row, in CSV_COLUMNS' order. */
function dealRow(d: DealData, stages: Stage[]): string[] {
  const iso = (ms: number | null) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");
  const history = [...d.notes]
    .reverse()
    .map((n) => `${iso(n.at)} ${NOTE_WORDS[n.kind]}: ${n.text}${n.image ? `${n.text ? " " : ""}(picture ${picturePath(n.image.key)})` : ""}`)
    .join(" | ");
  return [d.company, d.contact, d.email, d.phone, d.value === null ? "" : String(d.value), stageOf(d, stages).name, d.status === "open" ? "Open" : d.status === "won" ? "Won" : "Lost", d.next, d.due ?? "", iso(d.closedAt), history];
}

/** Deals as a CSV file, a header row first. */
export function toCsv(deals: Deal[], stages: Stage[]): string {
  return [CSV_COLUMNS, ...deals.map((d) => dealRow(d.data, stages))].map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";
}

/** A CSV file's rows, quotes and all. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let f = "";
  let q = false;
  const s = text.replace(/^﻿/, "");
  // a file saved with semicolons (Excel in much of Europe) reads the same
  const firstLine = s.slice(0, s.indexOf("\n") < 0 ? undefined : s.indexOf("\n"));
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : firstLine.includes("\t") && !firstLine.includes(",") ? "\t" : ",";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"' && s[i + 1] === '"') {
        f += '"';
        i++;
      } else if (c === '"') q = false;
      else f += c;
    } else if (c === '"' && !f) q = true;
    else if (c === sep) {
      row.push(f);
      f = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      row.push(f);
      f = "";
      if (row.some((x) => x.trim())) rows.push(row);
      row = [];
    } else f += c;
  }
  row.push(f);
  if (row.some((x) => x.trim())) rows.push(row);
  return rows;
}

const HEADS: Record<string, keyof DealData | "closed" | "history"> = {
  company: "company",
  organization: "company",
  organisation: "company",
  account: "company",
  business: "company",
  client: "company",
  deal: "company",
  "deal name": "company",
  name: "company",
  title: "company",
  contact: "contact",
  "contact name": "contact",
  person: "contact",
  "full name": "contact",
  email: "email",
  "e-mail": "email",
  "email address": "email",
  phone: "phone",
  "phone number": "phone",
  mobile: "phone",
  value: "value",
  "deal value": "value",
  amount: "value",
  worth: "value",
  price: "value",
  stage: "stage",
  status: "status",
  "next step": "next",
  "next action": "next",
  next: "next",
  "follow up": "next",
  "follow-up": "next",
  date: "due",
  "next step date": "due",
  due: "due",
  "due date": "due",
  "follow up date": "due",
  "follow-up date": "due",
  "won or lost on": "closed",
  "closed on": "closed",
  "close date": "closed",
  history: "history",
  notes: "history",
  note: "history",
};

export interface CsvDeal {
  data: Partial<DealData>;
  /** the stage the file names, as it names it */
  stageName: string;
}

/**
 * Deals from a CSV file: its header row says which column is which (company, contact, value, stage, next step,
 * date and the rest, by their usual names). A row with no name is skipped. A contact with no company keeps the
 * contact. "Won" and "Lost" in a status column (or as the stage) close the deal.
 */
export function fromCsv(text: string, today: string): { deals: CsvDeal[]; skipped: number; columns: string[] } {
  const rows = parseCsv(text);
  if (!rows.length) return { deals: [], skipped: 0, columns: [] };
  const head = rows[0].map((h) => HEADS[h.trim().toLowerCase().replace(/[_]+/g, " ").replace(/\s+/g, " ")] ?? null);
  // "Name" is the company unless the file also has a company column: then it is the person
  if (head.filter((h) => h === "company").length > 1) {
    const first = head.indexOf("company");
    const named = rows[0].findIndex((h, i) => head[i] === "company" && /^(name|full name)$/i.test(h.trim()));
    if (named >= 0 && named !== first) head[named] = "contact";
    else if (named === first) {
      head[first] = "contact";
    }
  }
  const out: CsvDeal[] = [];
  let skipped = 0;
  for (const r of rows.slice(1)) {
    const get = (k: string) => {
      const i = head.indexOf(k as never);
      // a cell this board wrote with a quote before =, +, - or @ (so a spreadsheet never runs it) reads as it was
      return i >= 0 ? (r[i] ?? "").trim().replace(/^'(?=[=+\-@])/, "") : "";
    };
    const data: Partial<DealData> = { company: get("company").slice(0, 120), contact: get("contact").slice(0, 120), email: get("email").slice(0, 200), phone: get("phone").slice(0, 60), next: get("next").slice(0, 200) };
    if (!data.company && !data.contact) {
      skipped++;
      continue;
    }
    data.value = get("value") ? parseValue(get("value")) : null;
    const due = readDue(get("due"), today);
    data.due = typeof due === "string" ? due : null;
    const status = `${get("status")} ${get("stage")}`.toLowerCase();
    data.status = /\bwon\b/.test(status) ? "won" : /\blost\b/.test(status) ? "lost" : "open";
    if (data.status !== "open") {
      const c = readDue(get("closed"), today);
      data.closedAt = typeof c === "string" ? Date.parse(`${c}T12:00:00Z`) : null;
    }
    const history = get("history");
    if (history) data.notes = [{ id: "n_csv", at: 0, kind: "note", text: history.slice(0, 4000), by: null }];
    out.push({ data, stageName: get("stage") });
  }
  return { deals: out, skipped, columns: rows[0].filter((_, i) => head[i]) };
}

/** A stage by its name, any case, or by its id. */
export function stageNamed(stages: Stage[], name: string): Stage | null {
  const low = name.trim().toLowerCase();
  if (!low) return null;
  return stages.find((s) => s.id === name || s.name.toLowerCase() === low) ?? stages.find((s) => s.name.toLowerCase().startsWith(low)) ?? null;
}

// ---------------------------------------------------------------------------
// undo: a deal moved by one step's change, as it is now

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Lists of things with ids (a deal's notes, a pipeline's stages): what one step changed, applied to the list as it is now. */
function shiftList<T extends { id: string }>(cur: T[], from: T[], to: T[]): T[] {
  const fromIds = new Map(from.map((x) => [x.id, x]));
  const toIds = new Map(to.map((x) => [x.id, x]));
  let out = cur.filter((x) => !(fromIds.has(x.id) && !toIds.has(x.id)));
  out = out.map((x) => {
    const f = fromIds.get(x.id);
    const t = toIds.get(x.id);
    return f && t && !same(f, t) ? t : x;
  });
  // what the step added comes back where it stood: after the item before it in to
  to.forEach((x, i) => {
    if (fromIds.has(x.id) || out.some((y) => y.id === x.id)) return;
    const prev = i > 0 ? out.findIndex((y) => y.id === to[i - 1].id) : -1;
    out.splice(prev + 1, 0, x);
  });
  // an order the step changed, when the list still holds the same things
  if (!same(from.map((x) => x.id), to.map((x) => x.id)) && same([...from.map((x) => x.id)].sort(), [...to.map((x) => x.id)].sort()) && same([...out.map((x) => x.id)].sort(), [...to.map((x) => x.id)].sort())) {
    out = to.map((x) => out.find((y) => y.id === x.id)!);
  }
  return out;
}

/**
 * An entry that is not a page, moved by one step's change from one state to another, as it is now: only the
 * fields the step changed, and in a list of things with ids only the things it added, removed or changed. So
 * an undo never takes away what was written since (a note an agent logged after a drag stays when the drag is
 * undone).
 */
export function shiftData(cur: Entry, from: Entry, to: Entry): Entry {
  const data = { ...cur.data };
  let changed = false;
  for (const k of new Set([...Object.keys(from.data), ...Object.keys(to.data)])) {
    const f = from.data[k];
    const t = to.data[k];
    if (same(f, t) || same(data[k], t)) continue;
    const list = (v: unknown): v is { id: string }[] => Array.isArray(v) && v.every((x) => x && typeof x === "object" && typeof (x as { id?: unknown }).id === "string");
    // a field the step added and the state it goes back to lacks is sent cleared: left out, the board keeps what it had
    // (Features 2 review, 21 Oct: undoing an idea's new picture left it on the board)
    data[k] = list(f) && list(t) && list(data[k]) ? shiftList(data[k] as { id: string }[], f, t) : t === undefined ? null : t;
    if (!same(data[k], cur.data[k])) changed = true;
  }
  let { parent, order } = cur;
  if ((from.order !== to.order || (from.parent ?? null) !== (to.parent ?? null)) && (order !== to.order || (parent ?? null) !== (to.parent ?? null))) {
    parent = to.parent;
    order = to.order;
    changed = true;
  }
  return changed ? { ...cur, parent, order, data } : cur;
}

// ---------------------------------------------------------------------------
// an agent tidying a deal's history when the user asks (write_deal notes, Round 7)

/** What a list of note changes does to a deal's history, worked out before anything changes. */
export interface NotesPlan {
  /** the history after, in its order */
  notes: DealNote[];
  /** notes given new words, outside a merge */
  rewrote: number;
  /** merges, and the notes they made one of, their own notes among them */
  merges: number;
  mergedIn: number;
  /** notes taken out */
  removed: number;
  /** each note as it was before the change, for the trash: every one taken out or given new words */
  before: DealNote[];
}

const LINE_WORDS = (n: DealNote) => `"${n.text.slice(0, 60)}${n.text.length > 60 ? "…" : ""}"`;

/**
 * Changes an agent asked for to the notes already in a deal's history, by their ids as read_deal lists them: text
 * rewrites a note, merge folds other notes into it (it takes text, their words for all of them), remove takes one
 * out. A note keeps its id, date and kind, and an agent's words carry its name; the board's own lines (a move, a
 * win, a loss) stay as they are. A picture is never lost: a merge's note keeps its own or takes the first of those
 * it took in, and any other stays in the history on its own line. Anything it cannot read changes nothing.
 */
export function planNotes(notes: DealNote[], raw: unknown, by: string): NotesPlan {
  const how = "notes is a list of changes, each naming a note by its id as read_deal lists it: {id, text} rewrites it, {id, merge: [other ids], text} folds those notes into it, {id, remove: true} takes it out.";
  if (!Array.isArray(raw) || !raw.length || raw.some((c) => !c || typeof c !== "object" || Array.isArray(c))) throw new PipelineRefused(how);
  if (raw.length > 100) throw new PipelineRefused("Up to 100 changes to notes in one call.");
  const byId = new Map(notes.map((n) => [n.id, n]));
  const named = new Set<string>();
  const note = (id: unknown): DealNote => {
    const n = typeof id === "string" ? byId.get(id.trim()) : undefined;
    if (!n) throw new PipelineRefused(`No note in this deal's history has the id ${typeof id === "string" && id.trim() ? `"${id.trim().slice(0, 40)}"` : "given"}: read_deal lists each note with its id.`);
    if (n.kind === "moved") throw new PipelineRefused(`${n.id} is the board's own line (${LINE_WORDS(n)}): a move, a win or a loss stays as it is.`);
    if (named.has(n.id)) throw new PipelineRefused(`The note ${n.id} is named twice: name each note once, in one change.`);
    named.add(n.id);
    return n;
  };
  const words = (v: unknown): string | null => (typeof v === "string" ? v.trim().slice(0, 4000) : null);
  const out = new Map<string, DealNote | null>();
  const plan: NotesPlan = { notes, rewrote: 0, merges: 0, mergedIn: 0, removed: 0, before: [] };
  for (const c of raw as Record<string, unknown>[]) {
    const n = note(c.id);
    const text = words(c.text);
    const merge = c.merge === undefined || c.merge === null ? [] : c.merge;
    if (!Array.isArray(merge)) throw new PipelineRefused(`merge is a list of other notes' ids, for ${n.id}.`);
    if (c.remove === true) {
      if (text !== null || merge.length) throw new PipelineRefused(`The change to ${n.id} both removes it and gives it words: a change does one.`);
      out.set(n.id, null);
      plan.removed++;
      plan.before.push(n);
      continue;
    }
    if (text === null) throw new PipelineRefused(merge.length ? `merge needs text: the words for the one note ${n.id} and those notes make.` : `The change to ${n.id} says nothing to do: text rewrites it, merge with text folds other notes into it, remove true takes it out.`);
    const taken = merge.map(note);
    // a picture is never lost: the note keeps its own, or takes the first of those it takes in
    const image = n.image ?? taken.find((x) => x.image)?.image;
    if (!text && !image) throw new PipelineRefused(`A note needs words or a picture: text for ${n.id} is empty. remove true takes it out.`);
    for (const x of taken) {
      // one whose picture stays behind keeps its own line, with only the picture
      out.set(x.id, x.image && x.image !== image ? { ...x, text: "" } : null);
      plan.before.push(x);
    }
    if (taken.length) {
      plan.merges++;
      plan.mergedIn += taken.length + 1;
    }
    if (text === n.text && image === n.image) continue;
    out.set(n.id, { ...n, text, by, ...(image ? { image } : {}) });
    if (!taken.length) plan.rewrote++;
    plan.before.push(n);
  }
  plan.notes = notes.flatMap((n) => (out.has(n.id) ? (out.get(n.id) ? [out.get(n.id)!] : []) : [n]));
  return plan;
}

/**
 * Notes back from the trash, as they were (Round 7): one still in the history gets its words back, and one taken
 * out comes back where it stood in time. Notes written since stay.
 */
export function restoreNotes(current: DealNote[], saved: DealNote[]): DealNote[] {
  const back = new Map(saved.map((n) => [n.id, n]));
  const out = current.map((n) => back.get(n.id) ?? n);
  for (const n of saved) {
    if (current.some((x) => x.id === n.id)) continue;
    const at = out.findIndex((x) => x.at > n.at);
    out.splice(at < 0 ? out.length : at, 0, n);
  }
  return out;
}

/** What a plan did, in a few words: "rewrote 2 notes, merged 3 notes into 1 and removed 1"; "" when nothing. */
export function notesWords(p: Pick<NotesPlan, "rewrote" | "merges" | "mergedIn" | "removed">): string {
  const n = (k: number, one: string) => (k === 1 ? `1 ${one}` : `${k} ${one}s`);
  const merged = p.merges ? `merged ${n(p.mergedIn, "note")} into ${p.merges}` : "";
  const parts = [p.rewrote ? `rewrote ${n(p.rewrote, "note")}` : "", merged, p.removed ? `removed ${n(p.removed, "note")}` : ""].filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}` : (parts[0] ?? "");
}

// ---------------------------------------------------------------------------
// an agent's names for a pipeline's columns (update_board stages, Features 2)

/** What a list of column names does to a pipeline's stages, worked out before anything changes. */
export interface StagesPlan {
  /** the stages after, in order */
  stages: Stage[];
  /** columns kept under a new name */
  renamed: { from: string; to: string }[];
  /** new columns, by name */
  added: string[];
  /** columns taken away, none of which held a deal */
  removed: string[];
  /** the columns kept are in another order than before */
  reordered: boolean;
  /**
   * deals pointed again in the same step: a lost one whose column went, at the column before it, as the Stages
   * window does, with the same line in its history; one in a column whose stage id is not a column's (from before
   * a change), at the column it shows in now, so it never moves
   */
  relinked: { deal: Deal; to: Stage; note: string | null }[];
  changed: boolean;
}

const lower = (s: string) => s.toLowerCase();

/**
 * Every column's name in order, as an agent gives them, against the stages a pipeline has. A name a column has now
 * (any case) keeps that column; a new name renames the column that sat in its place (between the same kept
 * columns), or adds a column there when there is none left to rename; a column no name keeps is taken away, which
 * is refused while deals sit in it. The column won deals go to stays last and is never taken away: the last name
 * is its own. Ids never change, so every deal stays in its column with its history.
 */
export function planStages(current: Stage[], raw: unknown, deals: Deal[], newStageId: () => string): StagesPlan {
  if (!Array.isArray(raw) || raw.some((n) => typeof n !== "string")) throw new PipelineRefused('stages is every column\'s name, in order, as a list of words, e.g. ["New enquiry", "Agreeing terms", "Won"].');
  const names = (raw as string[]).map((n) => n.replace(/\s+/g, " ").trim());
  if (!names.length) throw new PipelineRefused("A pipeline needs at least one column.");
  if (names.length > MAX_STAGES) throw new PipelineRefused(`A pipeline has up to ${MAX_STAGES} columns, and that list has ${names.length}.`);
  const blank = names.findIndex((n) => !n);
  if (blank >= 0) throw new PipelineRefused(`Name ${blank + 1} in the list is empty: every column needs a name.`);
  const long = names.find((n) => n.length > 40);
  if (long) throw new PipelineRefused(`"${long.slice(0, 40)}..." is too long: a column's name is up to 40 characters.`);
  const twice = names.find((n, i) => names.findIndex((m) => lower(m) === lower(n)) !== i);
  if (twice) throw new PipelineRefused(`"${twice}" is in the list twice: each column needs its own name.`);
  const won = current.find((s) => s.won) ?? null;
  let openNames = names;
  if (won) {
    const last = names[names.length - 1];
    const at = names.findIndex((n) => lower(n) === lower(won.name));
    if (at >= 0 && at !== names.length - 1) throw new PipelineRefused(`"${won.name}" is the column won deals go to, so it stays last: put its name last, or end the list with a new name for it.`);
    const other = current.find((s) => !s.won && lower(s.name) === lower(last));
    if (other) throw new PipelineRefused(`The last column is the one won deals go to ("${won.name}" now). It stays last and is never taken away, so the list ends with its name or a new name for it, not "${other.name}", another column's name.`);
    if (names.length < 2) throw new PipelineRefused(`A pipeline keeps at least one column before "${won.name}", the one won deals go to: give at least two names, that one's last.`);
    openNames = names.slice(0, -1);
  }
  const open = current.filter((s) => s !== won);
  const byName = new Map(open.map((s) => [lower(s.name), s]));
  const named = openNames.map((n) => byName.get(lower(n)) ?? null);
  const kept = new Set(named.flatMap((s) => (s ? [s.id] : [])));
  // the columns no name keeps, by the kept column before each (none: the start), so a new name between the same
  // kept columns renames the one that was there
  const spare = new Map<string, Stage[]>();
  let prev = "";
  for (const s of open) {
    if (kept.has(s.id)) prev = s.id;
    else spare.set(prev, [...(spare.get(prev) ?? []), s]);
  }
  const stages: Stage[] = [];
  const renamed: StagesPlan["renamed"] = [];
  const added: string[] = [];
  prev = "";
  openNames.forEach((name, i) => {
    let s = named[i];
    if (s) prev = s.id;
    else s = spare.get(prev)?.shift() ?? null;
    if (!s) {
      added.push(name);
      stages.push({ id: newStageId(), name });
      return;
    }
    if (s.name !== name) renamed.push({ from: s.name, to: name });
    stages.push({ id: s.id, name });
  });
  if (won) {
    const name = names[names.length - 1];
    if (won.name !== name) renamed.push({ from: won.name, to: name });
    stages.push({ id: won.id, name, won: true });
  }
  const removed = [...spare.values()].flat();
  // a column with deals in it is never taken away: they are named, so the agent can move them first
  const held = removed.map((s) => ({ s, in: columnOf(deals, s, current) })).filter((x) => x.in.length);
  if (held.length) {
    const said = held.map((x) => `"${x.s.name}", which holds ${x.in.length === 1 ? "1 deal" : `${x.in.length} deals`} (${x.in.slice(0, 5).map((d) => dealName(d.data)).join(", ")}${x.in.length > 5 ? ", and more" : ""})`);
    throw new PipelineRefused(`The list leaves out ${said.join(", and ")}. A column goes only once it is empty: move ${held.length === 1 && held[0].in.length === 1 ? "that deal" : "those deals"} to another column first with write_deal (stage), or keep the column in the list, by its name or a new one in its place.`);
  }
  const ids = new Set(stages.map((s) => s.id));
  const after = new Map(stages.map((s) => [s.id, s]));
  const gone = new Set(removed.map((s) => s.id));
  const relinked: StagesPlan["relinked"] = [];
  for (const d of deals) {
    if (ids.has(d.data.stage)) continue;
    if (gone.has(d.data.stage)) {
      // a lost deal, or a won one in the won column, whose own column went (a deal in a column keeps it: such a
      // column never goes): to the column before it, as the Stages window does
      const i = current.findIndex((s) => s.id === d.data.stage);
      const to = current.slice(0, i).reverse().map((s) => after.get(s.id)).find((s): s is Stage => !!s) ?? stages[0];
      relinked.push({ deal: d, to, note: `Moved to ${to.name} when ${current[i].name} was taken away` });
    } else if (d.data.status !== "lost") {
      const was = stageOf(d.data, current);
      if (stageOf(d.data, stages).id !== was.id && after.has(was.id)) relinked.push({ deal: d, to: after.get(was.id)!, note: null });
    }
  }
  const order = (list: Stage[], keep: (id: string) => boolean) => list.flatMap((s) => (keep(s.id) ? [s.id] : [])).join(" ");
  const reordered = order(current, (id) => ids.has(id)) !== order(stages, (id) => current.some((s) => s.id === id));
  return { stages, renamed, added, removed: removed.map((s) => s.name), reordered, relinked, changed: !!(renamed.length || added.length || removed.length || reordered) };
}

// ---------------------------------------------------------------------------
// a deal as an agent reads it

/** A deal on one line, as read_board and write_deal say it: name, contact, value, stage, next step and date, id. */
/** "2 pictures in its history", for a line about a deal, so an agent knows read_deal shows them (Round 7); "" with none. */
export function picturesInHistory(d: DealData): string {
  const n = d.notes.filter((x) => x.image).length;
  return n ? `${n} picture${n === 1 ? "" : "s"} in its history` : "";
}

export function dealLine(d: Deal, stages: Stage[], currency: string, today: string): string {
  const x = d.data;
  const due = x.due ? `, ${x.due} (${dueWords(x, today)?.text.toLowerCase() ?? "done"})` : "";
  const where = x.status === "won" ? `won${x.closedAt ? ` ${new Date(x.closedAt).toISOString().slice(0, 10)}` : ""}` : x.status === "lost" ? `lost${x.closedAt ? ` ${new Date(x.closedAt).toISOString().slice(0, 10)}` : ""}` : stageOf(x, stages).name;
  const step = x.status !== "open" ? "" : x.next ? `; next: ${x.next}${due}` : x.due ? `; next step due${due}` : "; no next step";
  const last = [...x.notes].reverse().find((n) => n.kind !== "moved");
  const pics = picturesInHistory(x);
  return `${dealName(x)}${x.company && x.contact ? ` (${x.contact})` : ""}${x.value !== null ? `, ${money(x.value, currency)}` : ""}, ${where}${step}${last ? `; last: ${NOTE_WORDS[last.kind]}, ${new Date(last.at).toISOString().slice(0, 10)}: "${last.text.slice(0, 80)}${last.text.length > 80 ? "…" : ""}"` : ""}${pics ? `; ${pics}` : ""} (deal id ${d.id})`;
}

/** A pipeline as an agent reads it: what is open and what to follow up, then each stage with its deals. */
export function pipelineLines(entries: Entry[], now = Date.now(), cap = 150): string[] {
  const { data: sd } = stagesOf(entries);
  const deals = dealsOf(entries);
  const today = todayKey(now, sd.tz);
  const f = followUps(deals, today);
  const open = deals.filter((d) => d.data.status === "open");
  const ov = openValue(deals);
  const lines = [
    `Today is ${today} for the user. Values are in ${sd.currency}. Stages: ${sd.stages.map((s) => `"${s.name}"${s.won ? " (won deals)" : ""}`).join(", ")}.`,
    `${open.length} open deal${open.length === 1 ? "" : "s"}${ov !== null ? `, ${money(ov, sd.currency)} open` : ""}. To follow up: ${f.count ? [f.late.length ? `${f.late.length} late` : "", f.today.length ? `${f.today.length} due today` : "", f.none.length ? `${f.none.length} with no next step` : ""].filter(Boolean).join(", ") : "nothing"}.`,
  ];
  if (!deals.length) return [...lines, "No deals yet: write_deal with company or contact makes one."];
  let left = cap;
  for (const st of sd.stages) {
    const col = columnOf(deals, st, sd.stages);
    const t = totalOf(col);
    lines.push(`### ${st.name} (${col.length}${t !== null ? `, ${money(t, sd.currency)}` : ""})`);
    // the won stage shows its latest; an open stage shows every deal while there is room
    const shown = st.won ? [...col].sort((a, b) => (b.data.closedAt ?? 0) - (a.data.closedAt ?? 0)).slice(0, 20) : col;
    const list = shown.slice(0, Math.max(0, left));
    for (const d of list) lines.push(`- ${dealLine(d, sd.stages, sd.currency, today)}`);
    left -= list.length;
    if (list.length < col.length) lines.push(`- and ${col.length - list.length} more${st.won ? " won" : ""}: read_deal reads one by name.`);
  }
  const lost = deals.filter((d) => d.data.status === "lost").sort((a, b) => (b.data.closedAt ?? 0) - (a.data.closedAt ?? 0));
  if (lost.length) {
    lines.push(`### Lost (${lost.length})`);
    for (const d of lost.slice(0, 10)) lines.push(`- ${dealLine(d, sd.stages, sd.currency, today)}`);
    if (lost.length > 10) lines.push(`- and ${lost.length - 10} more.`);
  }
  return lines;
}
